/** DESK WIDGET LIBRARY — every widget is a normal tile. Honest data only. */
import React, { useEffect, useState, useCallback } from 'react';
import { TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, BORDER, HEAD, SPEC, mini } from './V2DeskTiles';

const useWidget = (req, path, ms = 60000) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => { req('GET', path).then(setData).catch(() => {}); }, [req, path]);
  useEffect(() => { load(); const t = setInterval(load, ms); return () => clearInterval(t); }, [load, ms]);
  return [data, load];
};

const MASKS = ['a local business', 'a Brisbane trade', 'a nearby café', 'a Bayside client'];
const maskIf = (demo, s) => demo ? MASKS[(String(s || '').length) % MASKS.length] : s;
const money = (demo, v) => demo ? '$•••' : `$${Number(v || 0).toLocaleString()}`;
const emptyP = { fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 };
const rowP = { margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const chip = (color) => ({ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, color, border: `1px solid ${color}44`, borderRadius: 999, padding: '1px 7px', flexShrink: 0 });
const ago = (ts) => {
  const d = (Date.now() - new Date(ts).getTime()) / 3600000;
  return d < 1 ? `${Math.max(1, Math.round(d * 60))}m` : d < 24 ? `${Math.round(d)}h` : `${Math.round(d / 24)}d`;
};

export const ClockTile = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div data-testid="widget-clock" style={{ textAlign: 'center', paddingTop: 2 }}>
      <p style={{ margin: 0, fontFamily: HEAD, fontSize: 30, fontWeight: 800, color: TXT, letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' }}>
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </p>
      <p style={{ margin: '2px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB }}>
        {now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>
    </div>
  );
};

export const WinsWeekTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/wins_week');
  if (!d) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-winsweek" style={{ textAlign: 'center' }}>
      <p style={{ margin: 0, fontFamily: HEAD, fontSize: 30, fontWeight: 800, backgroundImage: SPEC, WebkitBackgroundClip: 'text', color: 'transparent' }}>{d.count}</p>
      <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB }}>wins this week{d.value > 0 ? <b style={{ color: AMBER }}> · {money(ctx.demo, d.value)}</b> : ''}</p>
    </div>
  );
};

export const SendsTodayTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/sends_today');
  if (!d) return <p style={emptyP}>…</p>;
  const pct = Math.min(100, (d.sent / Math.max(1, d.cap)) * 100);
  return (
    <div data-testid="widget-sendstoday">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <p style={{ margin: 0, fontFamily: HEAD, fontSize: 20, fontWeight: 800, color: TXT }}>{d.sent}<span style={{ fontSize: 12, color: SUB }}> / {d.cap}</span></p>
        <span style={chip(pct >= 100 ? MAGENTA : TEAL)}>{pct >= 100 ? 'cap hit' : 'warming'}</span>
      </div>
      <div style={{ height: 7, marginTop: 8, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: SPEC, transition: 'width 600ms ease' }} />
      </div>
      <p style={{ ...emptyP, marginTop: 6, fontSize: 10 }}>sends today vs warm-up cap</p>
    </div>
  );
};

export const UnpaidInvoicesTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/unpaid_invoices', 300000);
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return (
    <div data-testid="widget-invoices-unconfigured" style={{ textAlign: 'center', padding: '8px 4px' }}>
      <p style={{ margin: 0, fontSize: 20 }}>🔗</p>
      <p style={{ ...emptyP, marginTop: 4 }}>{d.reason}</p>
    </div>
  );
  return (
    <div data-testid="widget-invoices" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <p style={{ margin: '0 0 6px', fontFamily: HEAD, fontSize: 18, fontWeight: 800, color: AMBER }}>{money(ctx.demo, d.total_due)} <span style={{ fontSize: 10.5, color: SUB, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600 }}>owed</span></p>
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {d.invoices.map((i, k) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
            <p style={rowP}>{maskIf(ctx.demo, i.contact)} <span style={{ color: SUB, fontSize: 9.5 }}>{i.number}</span></p>
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, flexShrink: 0 }}>{money(ctx.demo, i.amount_due)}</b>
            <span style={chip(i.days_overdue > 30 ? MAGENTA : i.days_overdue > 0 ? AMBER : TEAL)}>{i.days_overdue > 0 ? `${i.days_overdue}d over` : 'current'}</span>
          </div>
        ))}
        {d.invoices.length === 0 && <p style={emptyP}>Nothing unpaid. 🎉</p>}
      </div>
    </div>
  );
};

export const EnquiriesTile = ({ ctx }) => {
  const [d, reload] = useWidget(ctx.req, '/api/admin/desk/widgets/enquiries');
  if (!d) return <p style={emptyP}>…</p>;
  const promote = async (e) => {
    const r = await ctx.req('POST', '/api/admin/desk/widgets/enquiries/promote', { email: e.email, name: e.name, source: e.source });
    ctx.showToast(r.existing ? '✓ Lead already exists' : `＋ Lead created for ${ctx.demo ? 'enquiry' : e.name}`);
    reload();
  };
  const SRC = { intake: [TEAL, '📥'], discover: [VIOLET, '🧭'], contact: [AMBER, '✉'] };
  return (
    <div data-testid="widget-enquiries" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {d.enquiries.map((e, k) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
          <span style={{ fontSize: 11, flexShrink: 0 }}>{(SRC[e.source] || [SUB, '•'])[1]}</span>
          <p style={rowP}>{maskIf(ctx.demo, e.name)}</p>
          <span style={chip((SRC[e.source] || [SUB])[0])}>{e.source}</span>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, flexShrink: 0 }}>{ago(e.ts)}</span>
          {e.lead_exists
            ? <span style={chip(TEAL)}>✓ lead</span>
            : e.email && <button data-testid={`enquiry-promote-${k}`} onClick={() => promote(e)} className="no-drag" style={{ ...mini, padding: '1px 8px', fontSize: 9.5, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>＋ lead</button>}
        </div>
      ))}
      {d.enquiries.length === 0 && <p style={emptyP}>No enquiries yet — the site is listening.</p>}
    </div>
  );
};

export const AwaitingReplyTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/awaiting_reply');
  if (!d) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-awaiting" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {d.awaiting.map((a, k) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
          <p style={rowP}>{ctx.dm(a.name)}{a.business ? <span style={{ color: SUB, fontSize: 10 }}> · {ctx.dm(a.business)}</span> : ''}</p>
          <span style={chip(a.heat === 'hot' ? MAGENTA : a.heat === 'warm' ? AMBER : TEAL)}>{Math.round(a.days)}d</span>
          <button data-testid={`awaiting-followup-${k}`} onClick={() => ctx.composeFor(a.lead_id)} className="no-drag"
            style={{ ...mini, padding: '1px 8px', fontSize: 9.5, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>✉ follow up</button>
        </div>
      ))}
      {d.awaiting.length === 0 && <p style={emptyP}>Everyone's replied. Rare air.</p>}
    </div>
  );
};

export const PromisesTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/promises');
  if (!d) return <p style={emptyP}>…</p>;
  const U = { past: MAGENTA, hot: MAGENTA, warm: AMBER, fresh: TEAL };
  const label = (p) => p.urgency === 'past' ? `${Math.abs(Math.round(p.days_left))}d late` : p.days_left < 1 ? 'today' : `in ${Math.round(p.days_left)}d`;
  return (
    <div data-testid="widget-promises" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {d.promises.map((p) => (
        <div key={p.item_id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)', boxShadow: p.urgency === 'past' || p.urgency === 'hot' ? `inset 0 0 0 1px ${U[p.urgency]}44` : 'none' }}>
          <p style={rowP}>{ctx.dm(p.title)}</p>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, flexShrink: 0 }}>📅 {p.due_date?.slice(5)}</span>
          <span style={chip(U[p.urgency])}>{label(p)}</span>
        </div>
      ))}
      {d.promises.length === 0 && <p style={emptyP}>No dated promises. Add a due date to any task and it lands here.</p>}
    </div>
  );
};

/** CALENDAR — custom-rendered, never an embedded calendar. */
export const CalendarTile = ({ ctx }) => {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [d, setD] = useState(null);
  const [nowMin, setNowMin] = useState(new Date().getHours() * 60 + new Date().getMinutes());
  useEffect(() => {
    let live = true;
    ctx.req('GET', `/api/admin/desk/widgets/calendar?day=${day}`).then((x) => live && setD(x)).catch(() => {});
    const t = setInterval(() => setNowMin(new Date().getHours() * 60 + new Date().getMinutes()), 30000);
    return () => { live = false; clearInterval(t); };
  }, [ctx.req, day]);

  const today = new Date().toISOString().slice(0, 10);
  const week = [...Array(7)].map((_, i) => {
    const dt = new Date(); dt.setDate(dt.getDate() - dt.getDay() + 1 + i);
    return dt.toISOString().slice(0, 10);
  });
  const mins = (s) => { const [h, m] = (s || '').slice(11, 16).split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const fmt = (s) => (s || '').slice(11, 16);
  const events = (d?.events || []).map((e) => ({ ...e, m: mins(e.start) })).sort((a, b) => a.m - b.m);
  const next = day === today ? events.find((e) => e.m >= nowMin) : null;
  const W0 = 7 * 60, W1 = 19 * 60;
  const pos = (m) => Math.max(0, Math.min(100, ((m - W0) / (W1 - W0)) * 100));

  const weekStrip = (
    <div className="no-drag" style={{ display: 'flex', gap: 3, marginBottom: 8 }}>
      {week.map((w) => (
        <button key={w} data-testid={`cal-day-${w}`} onClick={() => setDay(w)}
          style={{ flex: 1, padding: '3px 0', borderRadius: 8, cursor: 'pointer', border: `1px solid ${w === day ? 'rgba(84,209,151,0.5)' : 'transparent'}`, background: w === day ? 'rgba(84,209,151,0.1)' : 'transparent', textAlign: 'center' }}>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, color: w === today ? TEAL : SUB, display: 'block' }}>{['M', 'T', 'W', 'T', 'F', 'S', 'S'][week.indexOf(w)]}</span>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 700, color: w === day ? TXT : SUB }}>{Number(w.slice(8))}</span>
        </button>
      ))}
    </div>
  );

  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return (
    <div data-testid="widget-calendar-permission" style={{ padding: '6px 4px' }}>
      {weekStrip}
      <div style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 12, border: `1px dashed ${BORDER}` }}>
        <p style={{ margin: 0, fontSize: 18 }}>🔐</p>
        <p style={{ margin: '4px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: 700, color: AMBER }}>Calendar needs one permission</p>
        <p style={{ ...emptyP, marginTop: 4, fontSize: 10, lineHeight: 1.5 }}>{d.reason}</p>
      </div>
    </div>
  );
  return (
    <div data-testid="widget-calendar" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {weekStrip}
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative', paddingLeft: 6 }}>
        <div style={{ position: 'absolute', left: 44, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.06)' }} />
        {day === today && nowMin >= W0 && nowMin <= W1 && (
          <div data-testid="cal-now-line" style={{ position: 'absolute', left: 0, right: 0, top: `${pos(nowMin)}%`, height: 2, background: SPEC, borderRadius: 2, boxShadow: `0 0 10px ${MAGENTA}66`, zIndex: 2 }}>
            <span style={{ position: 'absolute', left: 0, top: -4, width: 8, height: 8, borderRadius: '50%', background: MAGENTA, boxShadow: `0 0 8px ${MAGENTA}` }} />
          </div>
        )}
        {events.map((e, k) => {
          const isNext = next && e.m === next.m && e.title === next.title;
          const soon = day === today && e.m - nowMin > 0 && e.m - nowMin < 60;
          const cd = isNext ? (e.m - nowMin <= 0 ? 'now' : e.m - nowMin < 60 ? `in ${e.m - nowMin}m` : `in ${Math.round((e.m - nowMin) / 60 * 10) / 10}h`) : null;
          return (
            <div key={k} data-testid={`cal-event-${k}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 8px', margin: '4px 0', borderRadius: 10, position: 'relative', zIndex: 1,
              background: isNext ? 'rgba(236,124,36,0.13)' : 'rgba(255,255,255,0.035)',
              border: isNext ? '1px solid transparent' : `1px solid ${BORDER}`,
              backgroundImage: isNext ? `linear-gradient(rgba(23,24,27,0.97), rgba(23,24,27,0.97)), ${SPEC}` : 'none',
              backgroundOrigin: 'border-box', backgroundClip: isNext ? 'padding-box, border-box' : 'border-box',
              boxShadow: soon ? `0 0 14px ${MAGENTA}33` : 'none' }}>
              <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: isNext ? TEAL : SUB, width: 34, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{fmt(e.start)}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ ...rowP, fontWeight: isNext ? 800 : 600 }}>{ctx.demo ? 'Meeting — client' : e.title}</p>
                <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB }}>{fmt(e.start)}–{fmt(e.end)}{e.location && !ctx.demo ? ` · ${e.location}` : ''}{d.source === 'test' ? ' · TEST' : ''}</p>
              </div>
              {cd && <span data-testid="cal-next-countdown" style={chip(TEAL)}>{cd}</span>}
            </div>
          );
        })}
        {events.length === 0 && (
          <div data-testid="cal-empty" style={{ textAlign: 'center', padding: '18px 6px' }}>
            <p style={{ margin: 0, fontSize: 18 }}>☀️</p>
            <p style={{ ...emptyP, marginTop: 4 }}>Clear day — good building weather.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── GoHighLevel widgets — fed by /widgets/ghl_enquiries + /widgets/ghl_campaigns
export const GhlEnquiriesTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/ghl_enquiries');
  const [added, setAdded] = React.useState(null);
  React.useEffect(() => {
    ctx.req('GET', '/api/admin/desk/actions/ghl_enquiry_added')
      .then((r) => setAdded(new Set(r.added || [])))
      .catch(() => setAdded(new Set()));
  }, [ctx]);
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return <p style={emptyP}>{d.reason || 'GoHighLevel not connected.'}</p>;
  const copy = (v, kind) => { navigator.clipboard?.writeText(v || ''); ctx.showToast(`${kind} copied`); };
  const addToDesk = async (e) => {
    try {
      const r = await ctx.req('POST', '/api/admin/desk/actions/ghl_enquiry_add', {
        ghl_contact_id: e.id, name: e.name, business: e.business,
        phone: e.phone, email: e.email, source: e.source,
      });
      setAdded((prev) => new Set([...(prev || []), e.id]));
      ctx.showToast(r.already_existed ? `Already on Desk — ${e.name}` : `Added to Desk — ${e.name}`);
    } catch (err) {
      ctx.showToast(`Add failed: ${err.message}`);
    }
  };
  return (
    <div data-testid="widget-ghl-enquiries" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {d.enquiries.map((e, k) => {
        const isAdded = added && added.has(e.id);
        return (
          <div key={e.id || k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
            <span style={{ fontSize: 11, flexShrink: 0 }}>📥</span>
            <p style={rowP}>
              {maskIf(ctx.demo, e.name)}
              {e.business ? <span style={{ color: SUB, fontSize: 10 }}> · {ctx.dm(e.business)}</span> : ''}
            </p>
            {e.stage && e.stage !== 'new' && <span style={chip(VIOLET)}>{e.stage}</span>}
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, flexShrink: 0 }}>{ago(e.created_at)}</span>
            {e.email && (
              <button data-testid={`ghl-enq-copy-email-${k}`} onClick={() => copy(e.email, 'Email')} className="no-drag"
                title={e.email} style={{ ...mini, padding: '1px 6px', fontSize: 9.5, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>✉</button>
            )}
            {e.phone && (
              <button data-testid={`ghl-enq-copy-phone-${k}`} onClick={() => copy(e.phone, 'Phone')} className="no-drag"
                title={e.phone} style={{ ...mini, padding: '1px 6px', fontSize: 9.5, color: AMBER, borderColor: 'rgba(236,124,36,0.4)' }}>☎</button>
            )}
            <button data-testid={`ghl-enq-adddesk-${k}`}
              onClick={() => !isAdded && addToDesk(e)}
              disabled={!!isAdded}
              className="no-drag"
              title={isAdded ? 'Already on the Desk' : 'Add to Desk — creates a follow-up task'}
              style={{ ...mini, padding: '1px 6px', fontSize: 9.5,
                       color: isAdded ? SUB : MAGENTA,
                       borderColor: isAdded ? 'rgba(255,255,255,0.1)' : 'rgba(211,113,255,0.4)',
                       cursor: isAdded ? 'default' : 'pointer', opacity: isAdded ? 0.6 : 1 }}>
              {isAdded ? 'Added ✓' : '＋ Desk'}
            </button>
          </div>
        );
      })}
      {d.enquiries.length === 0 && <p style={emptyP}>No new enquiries yet — GoHighLevel is listening.</p>}
    </div>
  );
};

export const GhlCampaignsTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/ghl_campaigns');
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return <p style={emptyP}>{d.reason || 'GoHighLevel not connected.'}</p>;
  const statColor = { active: TEAL, running: TEAL, paused: AMBER, draft: SUB, archived: SUB };
  return (
    <div data-testid="widget-ghl-campaigns" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {d.campaigns.map((c, k) => (
        <div key={c.id || k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
          <span style={{ fontSize: 11, flexShrink: 0 }}>📣</span>
          <p style={rowP}>{ctx.dm(c.name || 'Untitled')}</p>
          {c.status && <span style={chip(statColor[String(c.status).toLowerCase()] || SUB)}>{c.status}</span>}
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, flexShrink: 0 }}>
            {c.sent != null ? `${c.sent} sent` : ''}
            {c.opened != null ? ` · ${c.opened} opened` : ''}
            {c.replied != null ? ` · ${c.replied} replied` : ''}
          </span>
        </div>
      ))}
      {d.campaigns.length === 0 && <p style={emptyP}>No campaigns running — quiet air.</p>}
    </div>
  );
};



// GHL Pipeline — real opportunities from Col's active GoHighLevel pipeline.
// Renders stages dynamically from the pipeline definition (not hardcoded).
export const GhlPipelineTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/ghl_pipeline');
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return <p style={emptyP}>{d.reason || 'GoHighLevel not connected.'}</p>;
  const stages = d.stages || [];
  if (!stages.length) return <p style={emptyP}>{d.reason || 'No opportunities yet — pipeline is quiet.'}</p>;
  const max = Math.max(1, ...stages.map((s) => s.count || 0));
  const STAGE_COLORS = [SUB, '#7BB0EC', TEAL, VIOLET, AMBER, MAGENTA, TEAL, MAGENTA];
  const money = (v) => v ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null;
  return (
    <div data-testid="widget-ghl-pipeline">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: TXT }}>{d.pipeline_name || 'Pipeline'}</span>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>{d.total} open · {money(d.total_value) || '$0'}</span>
      </div>
      {stages.map((s, k) => {
        const color = STAGE_COLORS[k % STAGE_COLORS.length];
        return (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 700, color: SUB, width: 90, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.name}>{s.name}</span>
            <div style={{ flex: 1, height: 12, borderRadius: 6, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
              <div style={{ width: `${((s.count || 0) / max) * 100}%`, height: '100%', borderRadius: 6, background: `linear-gradient(90deg, ${color}66, ${color})`, transition: 'width 700ms ease' }} />
            </div>
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, width: 24, flexShrink: 0 }}>{s.count || 0}</b>
            {s.value ? <span style={chip(AMBER)}>{money(s.value)}</span> : null}
          </div>
        );
      })}
    </div>
  );
};

// WorkflowMax — Fitting Bay tile. Real WFM data, no fake $.
// Col's WFM jobs don't carry Budget values so we deliberately lead with
// counts + hours rather than fabricating a WIP $ headline.
export const WfmBayTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/wfm_bay');
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return <p style={emptyP}>{d.reason || 'WorkflowMax not connected.'}</p>;
  const bay = d;
  const states = bay.by_state || [];
  const max = Math.max(1, ...states.map((s) => s.count || 0));
  const bh = Math.round((bay.hours_7d?.billable_minutes || 0) / 60);
  const ih = Math.round((bay.hours_7d?.internal_minutes || 0) / 60);
  const budget = bay.budget || {};
  return (
    <div data-testid="widget-wfm-bay" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        {budget.total_declared ? (
          <>
            <span style={{ fontFamily: 'Newsreader, serif', fontStyle: 'italic', fontSize: 30, fontWeight: 400, color: AMBER, lineHeight: 1 }}>${Math.round(budget.total_declared).toLocaleString()}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>declared across {budget.jobs_with_budget}/{budget.jobs_active} jobs</span>
          </>
        ) : (
          <>
            <span style={{ fontFamily: 'Newsreader, serif', fontStyle: 'italic', fontSize: 30, fontWeight: 400, color: AMBER, lineHeight: 1 }}>{bay.active_jobs || 0}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>active jobs · {bh}h billable / {ih}h internal (7d)</span>
          </>
        )}
      </div>
      {states.length === 0 && <p style={emptyP}>No active jobs on the floor.</p>}
      {states.map((s, k) => {
        const color = [TEAL, VIOLET, AMBER, '#7BB0EC', MAGENTA, SUB][k % 6];
        return (
          <div key={s.state} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '3px 0' }}>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 700, color: SUB, width: 118, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.state}>{s.state}</span>
            <div style={{ flex: 1, height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
              <div style={{ width: `${((s.count || 0) / max) * 100}%`, height: '100%', borderRadius: 5, background: `linear-gradient(90deg, ${color}66, ${color})`, transition: 'width 700ms ease' }} />
            </div>
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, width: 22, flexShrink: 0 }}>{s.count || 0}</b>
          </div>
        );
      })}
      {budget.note ? (
        <p style={{ ...emptyP, marginTop: 6, fontSize: 9, color: SUB, fontStyle: 'italic' }}>{budget.note}</p>
      ) : null}
      {bay.next_due && bay.next_due.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: SUB }}>Next 5 due</span>
          {bay.next_due.map((r) => {
            const overdueColor = r.overdue ? MAGENTA : (r.days_out <= 3 ? AMBER : TEAL);
            const label = r.overdue
              ? `${Math.abs(r.days_out)}d overdue`
              : (r.days_out === 0 ? 'today' : `${r.days_out}d`);
            return (
              <div key={r.job_no} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 2px' }}>
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, color: SUB, width: 46, flexShrink: 0 }}>{r.job_no}</span>
                <p style={{ ...rowP, fontSize: 10.5 }} title={r.name}>
                  {ctx.dm(r.name)}
                  {r.client ? <span style={{ color: SUB, fontSize: 9.5 }}> · {ctx.dm(r.client)}</span> : ''}
                </p>
                <span style={chip(overdueColor)} title={r.due}>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Money · Pipelines — GHL + WFM as SEPARATE labelled lines. Never blends with
// the Xero money summary or the quote pipeline. Every line owns its source.
export const MoneyPipelinesTile = ({ ctx }) => {
  const [d] = useWidget(ctx.req, '/api/admin/desk/widgets/money_pipelines');
  if (!d) return <p style={emptyP}>…</p>;
  const lines = d.lines || [];
  if (!lines.length) return <p style={emptyP}>Connect GoHighLevel or WorkflowMax to see pipeline signals here.</p>;
  return (
    <div data-testid="widget-money-pipelines" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {lines.map((L) => (
        <div key={L.key} style={{ padding: '6px 4px 8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontFamily: 'Newsreader, serif', fontStyle: 'italic', fontSize: 22, fontWeight: 400, color: AMBER, lineHeight: 1 }}>{L.value_display}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 700, color: TXT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{L.label}</span>
          </div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, margin: '2px 0 0 0' }}>
            {L.sublabel} · <span style={{ color: SUB, opacity: 0.7 }}>from {L.source}</span>
          </p>
        </div>
      ))}
    </div>
  );
};

